import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Target, Lightbulb, Users, Briefcase, TrendingUp, Shield, Award, CheckCircle2, ArrowRight, Sparkles, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section with Background Image */}
      <section className="relative pt-24 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[85vh] sm:min-h-[90vh] flex items-center">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1600" 
            alt="Modern office" 
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 to-teal-800/90"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex justify-center mb-4">
              <Sparkles className="text-pink-300 animate-pulse" size={40} />
            </div>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 px-2"
              data-testid="hero-title"
              data-anim="typing"
              data-speed="35"
            >
              Connecting Talent with Opportunities
            </h1>
            <p
              className="text-base sm:text-lg md:text-xl text-teal-50 mb-6 sm:mb-8 max-w-3xl mx-auto px-4"
            >
              Where the right people meet <span 
                data-anim="rotating-text" 
                data-words="the right future|amazing opportunities|their dream careers|transformative growth|exceptional success"
                data-speed="2000"
                data-transition="500"
                className="font-semibold text-transparent text-2xl bg-clip-text bg-[linear-gradient(90deg,#fbbf24,#f59e0b,#fcd34d)] bg-[length:200%_100%] animate-[gradient-move_3s_linear_infinite]"
              >the right future</span>
            </p>
            <div 
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-4"
              data-anim="scale-in"
              data-delay="300"
            >
              <Link to="/services" data-testid="find-hire-button" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-white text-blue-600 hover:bg-blue-50 px-6 sm:px-8 transform hover:scale-105 transition-all shadow-xl">
                  <Zap className="mr-2" size={20} />
                  Find Your Next Great Hire
                </Button>
              </Link>
              <Link to="/careers" data-testid="explore-careers-button" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-blue-600 px-6 sm:px-8 transform hover:scale-105 transition-all">                  Explore Career Opportunities
                </Button>
              </Link>
            </div>
            
            {/* Job Search - Transparent UI */}
            <div 
              className="max-w-3xl mx-auto px-2 sm:px-0"
              data-anim="fade-up"
              data-delay="450"
            >
              <div className="p-4 sm:p-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg">
                <form onSubmit={(e) => { e.preventDefault(); window.location.href = '/careers'; }} className="flex flex-col gap-3 sm:gap-4">
                  <div className="w-full">
                    <Input
                      placeholder="Job title, keywords, or company"
                      className="h-11 sm:h-12 w-full bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:bg-white/30 focus:border-white/50 text-sm sm:text-base"
                      data-testid="job-search-input"
                      data-anim="fade-right"
                    />
                  </div>
                  <div className="w-full">
                    <Input
                      placeholder="City, state, or remote"
                      className="h-11 sm:h-12 w-full bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:bg-white/30 focus:border-white/50 text-sm sm:text-base"
                      data-testid="location-input"
                      data-anim="fade-up"
                      data-delay="50"
                    />
                  </div>
                  <Button 
                    type="submit"
                    size="lg" 
                    className="w-full bg-white text-blue-600 hover:bg-white/90 h-11 sm:h-12 hover:ring-2 hover:ring-blue-400 transition-all text-sm sm:text-base" 
                    data-testid="search-button"
                    data-anim="fade-left"
                    data-delay="100"
                  >
                    <Search className="mr-2" size={20} />
                    Search
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Trusted by Industry Leaders */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 
              className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 flex flex-col sm:flex-row items-center justify-center gap-2"
              data-anim="fade-up"
            >
              <Award className="text-blue-600 animate-bounce" size={40} />
              <span>Trusted by Industry Leaders</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8" data-anim="list-stagger" data-stagger="120">
            <Card className="text-center border-2 border-blue-200 hover:border-blue-400 hover:shadow-xl hover:scale-105 transition-all duration-300 bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-8">
                <div
                  className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent mb-2"
                  data-anim="counter"
                  data-from="0"
                  data-to="14"
                  data-duration="1200"
                >
                  14+
                </div>
                <p className="text-gray-600 font-medium">Years in Talent Consulting</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-pink-200 hover:border-pink-400 hover:shadow-xl hover:scale-105 transition-all duration-300 bg-gradient-to-br from-pink-50 to-white">
              <CardContent className="p-8">
                <div
                  className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-orange-600 bg-clip-text text-transparent mb-2"
                  data-anim="counter"
                  data-from="0"
                  data-to="1628"
                  data-duration="1500"
                >
                  1,628
                </div>
                <p className="text-gray-600 font-medium">Strategic Placements</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-green-200 hover:border-green-400 hover:shadow-xl hover:scale-105 transition-all duration-300 bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-8">
                <div
                  className="text-4xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent mb-2"
                  data-anim="percent"
                  data-from="0"
                  data-to="97"
                  data-duration="1400"
                >
                  97.4%
                </div>
                <p className="text-gray-600 font-medium">Client Satisfaction Rate</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Our Services */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4"
              data-anim="fade-up"
            >
              Our Services
            </h2>
            <p 
              className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4"
              data-anim="fade-up"
              data-delay="100"
            >
              Comprehensive talent solutions tailored to your organization's unique needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8" data-anim="list-stagger" data-stagger="150">
            {/* 1. Permanent Staffing */}
            <Card className="border-none shadow-lg hover:shadow-2xl hover:ring-2 hover:ring-blue-400 transition-all duration-300 overflow-hidden group">
              <CardContent className="p-6">
                <div 
                  className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center mb-4"
                  data-anim="scale-in"
                >
                  <Users className="text-white" size={32} />
                </div>
                <h3 
                  className="text-xl font-semibold text-gray-900 mb-4"
                  data-anim="fade-up"
                  data-delay="100"
                >
                  Permanent Staffing
                </h3>
                <p 
                  className="text-gray-600 text-sm mb-4"
                  data-anim="fade-up"
                  data-delay="150"
                >
                  End-to-end permanent placement services for long-term business success.
                </p>
                <ul 
                  className="space-y-3 mb-4"
                  data-anim="list-stagger"
                  data-stagger="80"
                  data-delay="200"
                >
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Understanding your specific needs and culture</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Sourcing and interviewing qualified candidates</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Perfect technical and cultural fit alignment</span>
                  </li>
                </ul>
                <Link to="/services" className="text-green-600 font-medium hover:text-green-700 text-sm inline-flex items-center group">
                  Learn more <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={16} />
                </Link>
              </CardContent>
            </Card>
            
            {/* 2. Temporary and Contract Staffing */}
            <Card className="border-none shadow-lg hover:shadow-2xl hover:ring-2 hover:ring-blue-400 transition-all duration-300 overflow-hidden group">
              <CardContent className="p-6">
                <div 
                  className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-700 flex items-center justify-center mb-4"
                  data-anim="scale-in"
                >
                  <Briefcase className="text-white" size={32} />
                </div>
                <h3 
                  className="text-xl font-semibold text-gray-900 mb-4"
                  data-anim="fade-up"
                  data-delay="100"
                >
                  Temporary and Contract Staffing
                </h3>
                <p 
                  className="text-gray-600 text-sm mb-4"
                  data-anim="fade-up"
                  data-delay="150"
                >
                  Flexible staffing solutions for businesses that need agility and quick response.
                </p>
                <ul 
                  className="space-y-3 mb-4"
                  data-anim="list-stagger"
                  data-stagger="80"
                  data-delay="200"
                >
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-teal-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Short-term project support and seasonal work</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-teal-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Pre-screened professionals ready to start</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-teal-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Swift response to temporary staffing needs</span>
                  </li>
                </ul>
                <Link to="/services" className="text-teal-600 font-medium hover:text-teal-700 text-sm inline-flex items-center group">
                  Learn more <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={16} />
                </Link>
              </CardContent>
            </Card>
            
            {/* 3. Executive Search */}
            <Card className="border-none shadow-lg hover:shadow-2xl hover:ring-2 hover:ring-blue-400 transition-all duration-300 overflow-hidden group">
              <CardContent className="p-6">
                <div 
                  className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-4"
                  data-anim="scale-in"
                >
                  <Target className="text-white" size={32} />
                </div>
                <h3 
                  className="text-xl font-semibold text-gray-900 mb-4"
                  data-anim="fade-up"
                  data-delay="100"
                >
                  Executive Search
                </h3>
                <p 
                  className="text-gray-600 text-sm mb-4"
                  data-anim="fade-up"
                  data-delay="150"
                >
                  Identify and attract top-level talent for your organization's most critical leadership positions.
                </p>
                <ul 
                  className="space-y-3 mb-4"
                  data-anim="list-stagger"
                  data-stagger="80"
                  data-delay="200"
                >
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Meticulous and confidential approach</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Source leaders with vision and experience</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Shape your company's future with the right leaders</span>
                  </li>
                </ul>
                <Link to="/services" className="text-blue-600 font-medium hover:text-blue-700 text-sm inline-flex items-center group">
                  Learn more <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={16} />
                </Link>
              </CardContent>
            </Card>
            
            {/* 4. Consulting Services */}
            <Card className="border-none shadow-lg hover:shadow-2xl hover:ring-2 hover:ring-blue-400 transition-all duration-300 overflow-hidden group">
              <CardContent className="p-6">
                <div 
                  className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center mb-4"
                  data-anim="scale-in"
                >
                  <Lightbulb className="text-white" size={32} />
                </div>
                <h3 
                  className="text-xl font-semibold text-gray-900 mb-4"
                  data-anim="fade-up"
                  data-delay="100"
                >
                  Consulting Services
                </h3>
                <p 
                  className="text-gray-600 text-sm mb-4"
                  data-anim="fade-up"
                  data-delay="150"
                >
                  Optimize your talent acquisition strategies with expert guidance and insights.
                </p>
                <ul 
                  className="space-y-3 mb-4"
                  data-anim="list-stagger"
                  data-stagger="80"
                  data-delay="200"
                >
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-orange-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Best practices and employer branding</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-orange-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Enhanced candidate experience strategies</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="text-orange-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Strategic workforce planning and optimization</span>
                  </li>
                </ul>
                <Link to="/services" className="text-orange-600 font-medium hover:text-orange-700 text-sm inline-flex items-center group">
                  Learn more <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={16} />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Team Section with Image */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div 
              className="relative order-2 lg:order-1"
              data-anim="fade-right"
            >
              <img 
                src="https://images.unsplash.com/photo-1758873268631-fa944fc5cad2?w=800" 
                alt="Diverse team collaboration" 
                className="shadow-2xl transition-all duration-500 rounded-lg w-full"
                loading="lazy"
              />
              <div 
                className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-orange-600 text-white p-4 sm:p-6 shadow-xl rounded-lg"
                data-anim="scale-in"
                data-delay="300"
              >
                <p 
                  className="text-2xl sm:text-3xl font-bold"
                  data-anim="counter"
                  data-from="0"
                  data-to="15"
                  data-duration="1500"
                  data-delay="500"
                >
                  15+
                </p>
                <p className="text-xs sm:text-sm">Companies Served</p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6"
                data-anim="fade-left"
              >
                Building Tomorrow's Workforce Today
              </h2>
              <p 
                className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6"
                data-anim="fade-left"
                data-delay="100"
              >
                We partner with forward-thinking organizations to create diverse, high-performing teams that drive innovation and success.
              </p>
              <div 
                className="space-y-4"
                data-anim="list-stagger"
                data-stagger="120"
                data-delay="200"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Proven Expertise</h3>
                    <p className="text-sm text-gray-600">14+ years of excellence in talent acquisition</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Data-Driven Approach</h3>
                    <p className="text-sm text-gray-600">Leveraging analytics for better hiring decisions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Award className="text-teal-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Award-Winning Service</h3>
                    <p className="text-sm text-gray-600">Recognized for excellence and client satisfaction</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Sparkles 
            className="mx-auto mb-4 animate-bounce text-blue-200" 
            size={40}
            data-anim="scale-in"
          />
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 px-4"
            data-anim="fade-up"
            data-delay="100"
          >
            Ready to Transform Your Talent Strategy?
          </h2>
          <p 
            className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 text-blue-100 px-4"
            data-anim="fade-up"
            data-delay="200"
          >
            Partner with Symetrix and discover the difference strategic talent solutions can make.
          </p>
          <Link to="/contact" data-testid="partner-button" className="inline-block w-full sm:w-auto px-4">
            <Button 
              size="lg" 
              className="w-full sm:w-auto bg-white text-blue-600 hover:bg-blue-50 px-6 sm:px-8 transform hover:scale-105 transition-all shadow-xl"
              data-anim="scale-in"
              data-delay="300"
            >
              Partner with Symetrix
            </Button>
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
